import { UTCDate } from '@date-fns/utc';
import * as Sentry from '@sentry/react';
import { ColumnDef } from '@tanstack/react-table';
import { format, getTime, isSameYear } from 'date-fns';
import { AmountDisplay } from '../../../../../design-system/amount-display/amount-display';
import { AvatarNameDescription } from '../../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Stack } from '../../../../../design-system/stack/stack';
import { Tooltip } from '../../../../../design-system/tooltip/tooltip';
import { TransactionView } from '../../../../../types/entity-types';
import { formatCurrency } from '../../../../../utils/format-currency';
import { getNormalizedAmount } from '../../../../../utils/get-normalized-amount';

const MAX_ACCOUNT_LENGTH = 12;
const MAX_REFERENCE_LENGTH = 15;

const getShouldShowTooltip = (value: string, id: string) => {
  if (id === 'reference') {
    return value.length > MAX_REFERENCE_LENGTH;
  } else if (id === 'account') {
    return value.length > MAX_ACCOUNT_LENGTH;
  } else {
    return false;
  }
};

export const recentTransactionsTableColumnsDefinition: ColumnDef<TransactionView>[] = [
  {
    id: 'fspDate',
    accessorFn: (row) => getTime(row.fspDate),
    header: 'Date',
    cell: ({ row }) => {
      const transactionDate = row.original.fspDate;
      const currentYear = new UTCDate();
      const dateFormat = isSameYear(transactionDate, currentYear) ? 'dd MMM' : 'dd MMM yyyy';

      return (
        <p className="text-xs leading-[14px] tracking-[-0.02em] text-black">{format(transactionDate, dateFormat)}</p>
      );
    },
    enableGlobalFilter: false,
    enableSorting: false,
  },
  {
    id: 'reference',
    accessorFn: (row) => row.reference ?? row.description,
    header: () => {
      return <p className="text-seapoint-gray-text-secondary w-36 truncate text-xs">Reference</p>;
    },
    cell: ({ column, row }) => {
      const reference = row.original.reference ?? row.original.description;
      if (getShouldShowTooltip(reference, column.id)) {
        return (
          <Tooltip
            content={reference}
            trigger={<p className="max-w-36 truncate p-1 text-sm text-black">{reference}</p>}
          />
        );
      } else {
        return <p className="max-w-36 truncate p-1 text-sm text-black">{reference}</p>;
      }
    },
    enableSorting: false,
  },
  {
    id: 'account',
    header: 'Account',
    cell: ({ column, row }) => {
      const { name, logo } = {
        name: row.original.account.institution?.name,
        logo: row.original.account.institution?.logo,
      };

      const accountNameWithCurrency = `${name} (${row.original.account.currency})`;

      return (
        <div className="max-w-32 truncate">
          {getShouldShowTooltip(accountNameWithCurrency, column.id) ? (
            <Tooltip
              content={accountNameWithCurrency}
              trigger={
                <AvatarNameDescription
                  avatar={{ showAvatar: true, avatarUrl: logo, size: 'xs' }}
                  name={accountNameWithCurrency}
                />
              }
            />
          ) : (
            <AvatarNameDescription
              avatar={{ showAvatar: true, avatarUrl: logo, size: 'xs' }}
              name={accountNameWithCurrency}
            />
          )}
        </div>
      );
    },
    enableGlobalFilter: false,
    enableSorting: false,
  },
  {
    id: 'amount',
    accessorKey: 'amount',
    header: 'Amount',
    accessorFn: (row) => {
      return formatCurrency(Math.abs(row.amount));
    },
    cell: ({ row }) => {
      Sentry.addBreadcrumb({
        data: {
          transactionRow: row.original,
          argsForNormalizedAmount: {
            amount: row.original.amount,
            isCredit: row.original.isCredit,
          },
          argsForNormalizedBaseCurrencyAmount: {
            amount: row.original.baseCurrencyAmount,
            isCredit: row.original.isCredit,
          },
          message: 'Row data in the transaction data table. This gets logged before we call getNormalizedAmount',
        },
      });

      const normalizedAmount = getNormalizedAmount({ amount: row.original.amount, isCredit: row.original.isCredit });
      const normalizedBaseCurrencyAmount = getNormalizedAmount({
        amount: row.original.baseCurrencyAmount,
        isCredit: row.original.isCredit,
      });
      const shouldDisplayBaseCurrencyAmount = row.original.currency !== row.original.baseCurrency;

      return (
        <Stack align="end" space={2}>
          <AmountDisplay normalizedAmount={normalizedAmount} currency={row.original.currency} />

          {shouldDisplayBaseCurrencyAmount && (
            <AmountDisplay
              normalizedAmount={normalizedBaseCurrencyAmount}
              currency={row.original.baseCurrency}
              isSecondary
            />
          )}
        </Stack>
      );
    },
  },
];
